import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  Accordion, AccordionSummary, AccordionDetails, Chip, Divider, IconButton,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon, Close as CloseIcon,
  Psychology as PsychologyIcon,
} from '@mui/icons-material';

// Tài liệu giải thích logic tính 7 score của EA Trader_Psychology_Monitor.
// Nội dung port từ comment + công thức trong file Trader_Spychology_Monitor.mq5.
// Mục đích: trader/admin xem được "score này tính từ cái gì" và "đọc thế nào"
// mà không cần mở MQL5.

// ─── Helpers cho block công thức + cách đọc ──────────────────────────────────
function Formula({ children }) {
  return (
    <Box sx={{
      p: 1.5, my: 1,
      bgcolor: 'rgba(139,92,246,0.08)',
      borderLeft: '3px solid #8b5cf6',
      borderRadius: 0.5,
      fontFamily: 'monospace',
      fontSize: '0.78rem',
      color: 'text.primary',
      whiteSpace: 'pre-wrap',
    }}>
      {children}
    </Box>
  );
}

function ReadGuide({ items }) {
  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', fontWeight: 700, mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Cách đọc
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2.5, fontSize: '0.85rem', '& li': { mb: 0.5 } }}>
        {items.map((it, i) => <li key={i}>{it}</li>)}
      </Box>
    </Box>
  );
}

function FactorList({ factors }) {
  return (
    <Box sx={{ mt: 1 }}>
      <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', fontWeight: 700, mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Thành phần
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2.5, fontSize: '0.82rem', '& li': { mb: 0.4 } }}>
        {factors.map((f, i) => (
          <li key={i}>
            <Box component="span" sx={{ color: '#a78bfa', fontFamily: 'monospace', fontWeight: 600 }}>
              {f.weight}
            </Box>
            {' '}— <strong>{f.name}</strong>: {f.desc}
          </li>
        ))}
      </Box>
    </Box>
  );
}

function ScoreSection({ icon, title, color, summary, children }) {
  return (
    <Accordion
      disableGutters
      sx={{
        bgcolor: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.06)',
        '&:before': { display: 'none' },
        '&.Mui-expanded': { borderColor: `${color}66` },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
          <Box sx={{
            width: 32, height: 32, borderRadius: '50%',
            bgcolor: `${color}22`, color,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '1rem', fontWeight: 700,
          }}>
            {icon}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color }}>
              {title}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              {summary}
            </Typography>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

// ─── Main Dialog ──────────────────────────────────────────────────────────────
export default function PsychologyHelpDialog({ open, onClose }) {
  return (
    <Dialog
      open={open} onClose={onClose}
      maxWidth="md" fullWidth
      PaperProps={{ sx: { bgcolor: '#0f172a', backgroundImage: 'none' } }}
    >
      <DialogTitle sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        background: 'linear-gradient(90deg, #8b5cf622 0%, transparent 100%)',
      }}>
        <PsychologyIcon sx={{ color: '#a78bfa' }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            Logic tính cảm xúc & cách đọc
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Tài liệu nội bộ — port từ EA Trader_Psychology_Monitor v1.1
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5 }}>
        {/* ─── Tổng quan ──────────────────────────────────────────────── */}
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={{ fontSize: '0.95rem', mb: 1, fontWeight: 600 }}>
            Tổng quan
          </Typography>
          <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', lineHeight: 1.7 }}>
            EA quét lịch sử <strong>90 ngày</strong> gần nhất, ghép các deal (DEAL_ENTRY_IN/OUT) thành <strong>vị thế</strong> rồi gom thành <strong>cụm lệnh</strong> (cluster — các vị thế trùng thời gian mở/đóng).
            Sau đó tính 7 score 0–100 cho mỗi vị thế đã đóng dựa trên <strong>baseline cá nhân</strong> (median của 100 lệnh gần nhất, tách post-Win / post-Loss).
            Mỗi score đều có dạng <code style={{ fontFamily: 'monospace' }}>weighted_sum × 100</code> rồi clamp [0, 100].
          </Typography>
        </Box>

        <Divider sx={{ mb: 2.5, opacity: 0.15 }} />

        {/* ─── Mức cảnh báo ───────────────────────────────────────────── */}
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Mức cảnh báo (chip màu trên gauge)
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 2.5, flexWrap: 'wrap' }}>
          <Chip size="small" label="Bình thường" sx={{ bgcolor: '#22c55e22', color: '#4ade80', fontWeight: 600 }} />
          <Chip size="small" label="Cảnh báo" sx={{ bgcolor: '#fbbf2422', color: '#fbbf24', fontWeight: 600 }} />
          <Chip size="small" label="Báo động" sx={{ bgcolor: '#ef444422', color: '#f87171', fontWeight: 600 }} />
        </Box>
        <Box sx={{ pl: 2, fontSize: '0.82rem', color: 'text.secondary', mb: 3 }}>
          <Box sx={{ mb: 0.5 }}>
            • <strong>Score thường</strong> (Trả thù, Tham lam, Sợ hãi, Cụm, DCA, Biến động) — cao = xấu:
            <Box sx={{ pl: 2, color: 'text.disabled', fontFamily: 'monospace', fontSize: '0.78rem' }}>
              score &lt; ngưỡng × 0.6 → Bình thường<br/>
              score &lt; ngưỡng → Cảnh báo<br/>
              score ≥ ngưỡng → Báo động
            </Box>
          </Box>
          <Box>
            • <strong>Kỷ luật (đảo ngược)</strong> — cao = tốt:
            <Box sx={{ pl: 2, color: 'text.disabled', fontFamily: 'monospace', fontSize: '0.78rem' }}>
              score &gt; ngưỡng × 1.67 → Bình thường<br/>
              score &gt; ngưỡng → Cảnh báo<br/>
              score ≤ ngưỡng → Báo động
            </Box>
          </Box>
        </Box>

        {/* ─── 6 score chính (accordion) ─────────────────────────────── */}
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Chi tiết từng score
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>

          {/* Trả thù */}
          <ScoreSection
            icon="!" title="Trả thù (Revenge)" color="#ef4444"
            summary="Đo hành vi vào lệnh báo thù sau khi thua lỗ"
          >
            <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', mb: 1 }}>
              Chỉ tính khi <strong>vị thế trước đó là LỖ</strong>. Nếu trước đó thắng → score = 0.
            </Typography>
            <Formula>
{`Revenge = 100 × (
    0.30 × gap_norm        ← gap thời gian ngắn sau lỗ
  + 0.30 × lot_norm         ← lot lớn hơn LotAL bình thường
  + 0.20 × loss_streak/5    ← chuỗi thua dài
  + 0.20 × loss_30m_norm    ← tổng lỗ 30 phút trước cao
)`}
            </Formula>
            <FactorList factors={[
              { weight: '0.30', name: 'Gap thời gian',  desc: 'Vào lệnh tiếp theo bao nhanh sau khi đóng lệnh thua. Càng nhanh → càng "trả thù".' },
              { weight: '0.30', name: 'Volume',         desc: 'Lot vào lệnh so với median lot post-loss. Lot bự hơn → tăng score.' },
              { weight: '0.20', name: 'Chuỗi thua',     desc: 'Đếm liên tiếp các lệnh thua trước đó (max 5). Càng dài → càng nguy hiểm.' },
              { weight: '0.20', name: 'Lỗ 30 phút',     desc: 'Tổng |pnl_net| của các lệnh thua trong 30 phút trước. Càng lớn → càng cay cú.' },
            ]} />
            <ReadGuide items={[
              <>Score &gt; <strong>70</strong> (mặc định) = đang vào lệnh báo thù → cần dừng tay.</>,
              <>Thường xuất hiện sau chuỗi 2-3 lệnh thua, vào lệnh trong vòng vài phút với lot gấp đôi.</>,
              <>Phối hợp với <em>Discipline</em> thấp = trader đang mất kiểm soát.</>,
            ]} />
          </ScoreSection>

          {/* Tham lam */}
          <ScoreSection
            icon="$" title="Tham lam (Greed)" color="#f97316"
            summary="Đo hành vi over-trading sau khi thắng"
          >
            <Formula>
{`Greed = 100 × (
    0.35 × lot_up_w       ← lot lớn sau khi thắng
  + 0.25 × gap_fast_w     ← vào lệnh nhanh sau thắng
  + 0.20 × trades_30m     ← over-trading trong 30 phút
  + 0.20 × concurrent     ← mở nhiều lệnh đồng thời
)`}
            </Formula>
            <FactorList factors={[
              { weight: '0.35', name: 'Lot post-win',     desc: 'Lot tăng đột biến so với median post-win. Đặc trưng "thắng rồi nâng size".' },
              { weight: '0.25', name: 'Gap post-win',     desc: 'Khoảng cách thời gian từ lệnh thắng trước. Càng ngắn → đang FOMO.' },
              { weight: '0.20', name: 'Số lệnh 30 phút', desc: 'Đếm số lệnh đã mở trong 30 phút. Cao = over-trading.' },
              { weight: '0.20', name: 'Concurrent',      desc: 'Số vị thế đang mở cùng lúc tại thời điểm này. Cao = ôm nhiều rủi ro.' },
            ]} />
            <ReadGuide items={[
              <>Score &gt; <strong>70</strong> = đang trong "hot streak" → dễ lật ngược kết quả.</>,
              <>Thường thấy sau 2-3 lệnh thắng liên tiếp, lot gấp 2-3 lần bình thường.</>,
              <>Greed cao + ClusterRisk cao = tích lũy rủi ro nghiêm trọng.</>,
            ]} />
          </ScoreSection>

          {/* Sợ hãi */}
          <ScoreSection
            icon="?" title="Sợ hãi (Fear)" color="#3b82f6"
            summary="Đo hành vi do dự, chốt lời sớm, lot quá nhỏ"
          >
            <Formula>
{`Fear = 100 × (
    0.40 × hold_short    ← chốt lời sớm hơn bình thường
  + 0.35 × lot_small     ← lot quá nhỏ so với LotAL
  + 0.25 × gap_long      ← vào lệnh quá xa nhau
)`}
            </Formula>
            <FactorList factors={[
              { weight: '0.40', name: 'Hold time ngắn', desc: 'Chỉ tính khi lệnh THẮNG. So với median holding time của lệnh thắng (HoldW). Càng ngắn → càng sợ giữ lệnh.' },
              { weight: '0.35', name: 'Lot quá nhỏ',     desc: 'Lot nhỏ hơn LotAL nhiều. Đặc trưng "sợ thua nên giảm size".' },
              { weight: '0.25', name: 'Gap dài',         desc: 'Khoảng cách thời gian giữa các lệnh dài bất thường. Trader đang lưỡng lự.' },
            ]} />
            <ReadGuide items={[
              <>Score &gt; <strong>65</strong> (mặc định) = đang ngại rủi ro quá mức → mất cơ hội lớn.</>,
              <>Thường xuất hiện sau drawdown lớn, trader "sợ" và rút về size nhỏ.</>,
              <>Fear cao kéo dài → ăn nhỏ, thua to (vì size không cân bằng).</>,
            ]} />
          </ScoreSection>

          {/* Cluster Risk */}
          <ScoreSection
            icon="#" title="Rủi ro cụm lệnh (Cluster Risk)" color="#a855f7"
            summary="Đo độ rủi ro của 1 cụm lệnh (nhiều lệnh chồng chéo)"
          >
            <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', mb: 1 }}>
              Chỉ tính khi cụm có <strong>≥ 2 vị thế</strong>. Cụm = các vị thế có thời gian mở/đóng chồng lấp.
            </Typography>
            <Formula>
{`ClusterRisk = 100 × (
    0.20 × pos_count_norm   ← số vị thế trong cụm cao
  + 0.20 × max_concurrent   ← đỉnh số đồng thời cao
  + 0.20 × is_mixed_hedge   ← 1 nếu cụm có cả Buy + Sell
  + 0.25 × loss_norm        ← cụm đang lỗ nặng (chỉ khi lỗ)
  + 0.15 × duration_norm    ← cụm kéo dài hơn bình thường
)`}
            </Formula>
            <FactorList factors={[
              { weight: '0.20', name: 'Số vị thế',   desc: 'Số lệnh trong cụm so với median ClPos.' },
              { weight: '0.20', name: 'Max concurrent', desc: 'Đỉnh điểm số lệnh mở đồng thời trong cụm.' },
              { weight: '0.20', name: 'Mixed-hedge', desc: '1 nếu cụm có cả Buy lẫn Sell (hedge), 0 nếu thuần 1 hướng.' },
              { weight: '0.25', name: 'Tổng lỗ',     desc: '|pnl_net| của cụm so với median ClLossAbs (chỉ tính khi cụm lỗ).' },
              { weight: '0.15', name: 'Thời lượng',  desc: 'Cụm kéo dài bao lâu so với median ClDur.' },
            ]} />
            <ReadGuide items={[
              <>Score &gt; <strong>70</strong> = cụm lệnh đang bùng phát rủi ro → nên đóng bớt.</>,
              <>Mixed-hedge (Buy + Sell cùng lúc) luôn bị penalty +0.20 — coi như flag riêng.</>,
              <>Khi có vị thế đang mở, score này được tính LIVE từ open positions (không cần đợi đóng).</>,
            ]} />
          </ScoreSection>

          {/* Discipline */}
          <ScoreSection
            icon="✓" title="Kỷ luật (Discipline) — đảo ngược" color="#22c55e"
            summary="Composite ngược: cao = tốt, thấp = mất kiểm soát"
          >
            <Formula>
{`Discipline = 100 × (1 − (
    0.35 × Revenge/100
  + 0.25 × Greed/100
  + 0.15 × Fear/100
  + 0.25 × ClusterRisk/100
))`}
            </Formula>
            <ReadGuide items={[
              <>Là composite ngược của 4 score xấu. Trọng số cao nhất là <strong>Trả thù (35%)</strong> + <strong>Cụm (25%)</strong>.</>,
              <>Score &lt; <strong>40</strong> (mặc định) = mất kiểm soát nghiêm trọng → dừng giao dịch.</>,
              <>Score &gt; <strong>67</strong> (=40×1.67) = bình thường, kỷ luật tốt.</>,
              <>Sợ hãi chỉ kéo Discipline xuống <strong>15%</strong> — vì sợ hãi không nguy hiểm bằng tham lam/trả thù.</>,
            ]} />
          </ScoreSection>

          {/* DCA Stack */}
          <ScoreSection
            icon="≡" title="DCA chồng lệnh (DCA Stack)" color="#ec4899"
            summary="Phát hiện hành vi DCA dày đặc (Martingale-like)"
          >
            <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', mb: 1 }}>
              Chỉ tính khi cụm có <strong>≥ 3 vị thế cùng hướng</strong> (KHÔNG mixed-hedge). Dùng <strong>ADR Quét</strong> làm thước đo biên độ.
            </Typography>
            <Formula>
{`DCAStack = 100 × (
    0.35 × price_dense        ← bước giá < 20% × ADR20
  + 0.25 × time_fast          ← thời gian giữa < 15 phút
  + 0.15 × count_factor       ← (pos_count − 3) / 5
  + 0.25 × loss_norm          ← cụm đang lỗ nặng
)`}
            </Formula>
            <FactorList factors={[
              { weight: '0.35', name: 'Mật độ giá', desc: 'Bước giá trung bình giữa các lệnh DCA. Càng gần (< 20% ADR20) → càng dày.' },
              { weight: '0.25', name: 'Tần suất',   desc: 'Thời gian trung bình giữa các lệnh (< 15 phút = nhanh).' },
              { weight: '0.15', name: 'Số lệnh',    desc: 'Càng nhiều lệnh DCA cùng hướng → càng nguy hiểm.' },
              { weight: '0.25', name: 'Tổng lỗ',    desc: 'Cụm DCA đang lỗ bao nhiêu so với baseline.' },
            ]} />
            <ReadGuide items={[
              <>Score &gt; <strong>70</strong> = DCA chồng lệnh nghiêm trọng → rủi ro vỡ tài khoản cao.</>,
              <>Có chế độ <em>LIVE alert</em> riêng — báo NGAY khi đang mở DCA dày, không đợi cụm đóng.</>,
              <>Mixed-hedge (Buy + Sell) <strong>không</strong> tính DCA — đó thuộc Cluster Risk.</>,
            ]} />
          </ScoreSection>

          {/* Emotion Shift */}
          <ScoreSection
            icon="~" title="Biến động cảm xúc (Emotion Shift)" color="#fbbf24"
            summary="Đo độ thay đổi giữa 2 event liên tiếp"
          >
            <Formula>
{`EmotionShift = 100 × (
    0.4 × |ΔRevenge|/100
  + 0.3 × |ΔGreed|/100
  + 0.2 × |ΔFear|/100
  + 0.1 × |ΔClusterRisk|/100
)`}
            </Formula>
            <ReadGuide items={[
              <>Đo "biến động" cảm xúc giữa 2 closed event kề nhau, KHÔNG phải mức độ tuyệt đối.</>,
              <>Score &gt; <strong>40</strong> = trader đang chuyển trạng thái nhanh → bất ổn tâm lý.</>,
              <>Ví dụ: vừa thắng (Greed) → thua đậm (Revenge bùng phát) → EmotionShift cao.</>,
              <>Hữu ích để phát hiện "swing" cảm xúc — đỉnh điểm thường xảy ra trước drawdown.</>,
            ]} />
          </ScoreSection>

        </Box>

        <Divider sx={{ mb: 2.5, opacity: 0.15 }} />

        {/* ─── Baseline ──────────────────────────────────────────────── */}
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Baseline cá nhân
        </Typography>
        <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', lineHeight: 1.7, mb: 2.5 }}>
          EA cần <strong>≥ 10 lệnh đã đóng</strong> để tính baseline. Sau đó dùng <strong>median của 100 lệnh gần nhất</strong> làm chuẩn so sánh:
          <Box component="ul" sx={{ mt: 1, pl: 3, '& li': { mb: 0.3 } }}>
            <li><code>Lot</code>, <code>Hold</code>, <code>Gap</code> — median lot, holding time, gap thời gian</li>
            <li><code>LotAW / LotAL</code> — median lot sau Win / sau Loss (tách riêng vì hành vi khác nhau)</li>
            <li><code>GapAW / GapAL</code> — gap thời gian sau Win / sau Loss</li>
            <li><code>HoldW / HoldL</code> — holding time của lệnh Win / Loss</li>
            <li><code>ClPos, ClDur, ClMaxConc, ClLossAbs</code> — baseline cấp cụm lệnh</li>
          </Box>
          Mỗi nhóm cần &ge; 5 mẫu, nếu thiếu → fallback về median chung (Lot/Gap/Hold).
        </Typography>

        <Divider sx={{ mb: 2.5, opacity: 0.15 }} />

        {/* ─── ADR ──────────────────────────────────────────────────── */}
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          ADR — biên độ giá tham chiếu
        </Typography>
        <Box sx={{ pl: 2, fontSize: '0.85rem', color: 'text.secondary', lineHeight: 1.7, mb: 2 }}>
          Tính trung bình biên độ daily candle (D1) trên <strong>5/10/20 ngày</strong>, BỎ bar 0 (hôm nay chưa đóng):
          <Box sx={{ mt: 1 }}>
            • <strong>Biên độ quét</strong> = <code>high − low</code> (gồm wick) — dùng làm thước đo cho <em>DCA detection</em><br/>
            • <strong>Biên độ chạy</strong> = <code>|close − open|</code> (chỉ thân nến) — chỉ phục vụ hiển thị, phản ánh xu hướng thật trong ngày
          </Box>
          <Box sx={{ mt: 1, fontSize: '0.78rem', color: 'text.disabled' }}>
            Nếu chạy ≈ quét → <em>trending day</em>. Nếu chạy nhỏ hơn quét nhiều → <em>range/whipsaw day</em>.
          </Box>
        </Box>

        <Divider sx={{ mb: 2.5, opacity: 0.15 }} />

        {/* ─── Lưu ý ────────────────────────────────────────────────── */}
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Lưu ý quan trọng
        </Typography>
        <Box component="ul" sx={{ pl: 3, fontSize: '0.85rem', color: 'text.secondary', lineHeight: 1.7, '& li': { mb: 0.5 } }}>
          <li>EA gắn lên <strong>1 chart 1 symbol</strong>. Nếu master trade nhiều symbol, để <code>InpOnlyCurrentSymbol = false</code> để gom toàn bộ — nhưng ADR sẽ chỉ phản ánh symbol của chart.</li>
          <li><strong>Live score</strong> (khi có vị thế đang mở) tính từ open positions + last-closed context — fire alert TRƯỚC khi cluster đóng.</li>
          <li><strong>Closed score</strong> (lệnh đã đóng) chỉ recompute mỗi 5s khi <code>HistoryDealsTotal()</code> thay đổi — tránh rebuild liên tục.</li>
          <li>Không hỗ trợ <code>DEAL_ENTRY_INOUT</code> (reversal/netting) trong v1 — sẽ bỏ qua các deal này.</li>
        </Box>
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid rgba(255,255,255,0.08)', px: 3, py: 1.5 }}>
        <Button onClick={onClose} variant="outlined">Đóng</Button>
      </DialogActions>
    </Dialog>
  );
}
